// ─── Page plein écran : édition d'un MENU (grille jour × moment) ──
// Pattern « brouillon local » (comme MoPCal) : nom/moments/cases ne
// s'écrivent qu'au 💾, sauf le choix d'une case qui met à jour le brouillon
// immédiatement (mais n'écrit Firestore qu'au 💾 global, contrairement à
// MoPCal où le tap sur une case écrivait tout de suite).
import React, { useMemo, useState } from 'react';
import { View, Text, TextInput, TouchableOpacity, ScrollView, StyleSheet, Alert } from 'react-native';
import { PageHeader, SectionPliable, useUI, useT, useModifie, stylesForm } from '@mop/core';
import GrilleMenu from './ui/GrilleMenu';
import FeuilleCase from './FeuilleCase';
import {
  enregistrerMenu, momentsDuMenu, normaliserHeureRepas,
  affecterRepasCase, affecterRecetteCase, affecterLibreCase, viderCase,
} from './services/menus';

export default function EcranMenuGrille({ espaceId, menu, config, recettes, repasList, onFermer }) {
  const UI = useUI();
  const t = useT();
  const s = useMemo(() => makeStyles(UI), [UI]);

  const [nom, setNom] = useState(menu.nom || '');
  const [moments, setMoments] = useState(momentsDuMenu(menu, config));
  const [cases, setCases] = useState(menu.cases || {});
  const [caseOuverte, setCaseOuverte] = useState(null); // { jourIdx, momentId }

  const { modifie, capturer } = useModifie({ nom, moments, cases });

  const recettesById = useMemo(() => Object.fromEntries(recettes.map(r => [r.id, r])), [recettes]);
  const repasById = useMemo(() => Object.fromEntries(repasList.map(r => [r.id, r])), [repasList]);

  const libelleCase = (c) => {
    if (c.repasId) return repasById[c.repasId]?.nom || t('repasPlan.recetteSupprimee');
    if (c.recetteId) return recettesById[c.recetteId]?.nom || t('repasPlan.recetteSupprimee');
    return c.libre || '';
  };

  const enregistrer = async () => {
    try {
      const momentsProp = moments.map(mo => ({
        ...mo,
        heureDebut: normaliserHeureRepas(mo.heureDebut) || undefined,
        heureFin: normaliserHeureRepas(mo.heureFin) || undefined,
      }));
      await enregistrerMenu(espaceId, menu.id, { nom: nom.trim() || 'Menu', moments: momentsProp, cases });
      capturer();
    } catch (e) { Alert.alert(t('repas.erreur'), e?.message || ''); }
  };

  const cleOuverte = caseOuverte ? `${caseOuverte.jourIdx}_${caseOuverte.momentId}` : null;

  const enregistrerCase = (val) => {
    if (!caseOuverte) return;
    const { jourIdx, momentId } = caseOuverte;
    setCases(x => val.libre !== undefined ? affecterLibreCase(x, jourIdx, momentId, val.libre)
      : val.repasId ? affecterRepasCase(x, jourIdx, momentId, val.repasId, val.personnes)
      : affecterRecetteCase(x, jourIdx, momentId, val.recetteId, val.personnes));
    setCaseOuverte(null);
  };
  const viderCaseOuverte = () => {
    if (!caseOuverte) return;
    setCases(x => viderCase(x, caseOuverte.jourIdx, caseOuverte.momentId));
    setCaseOuverte(null);
  };

  const setMoment = (i, k, v) => setMoments(x => x.map((mo, j) => j === i ? { ...mo, [k]: v } : mo));
  const ajouterMoment = () => setMoments(x => [...x, { id: 'm' + Math.random().toString(36).slice(2, 8), label: '' }]);
  const retirerMoment = (i) => setMoments(x => x.filter((_, j) => j !== i));

  return (
    <View style={{ flex: 1, backgroundColor: UI.bg }}>
      <PageHeader titre={nom || t('repasPlan.menu')} iconeTitre="📆" onRetour={onFermer}
        action={{ icone: '💾', label: t('commun.enregistrer'), disabled: !modifie || moments.length === 0, onPress: enregistrer }} />

      <ScrollView contentContainerStyle={s.corps}>
        <Text style={s.lbl}>{t('commun.nom')}</Text>
        <TextInput style={s.input} value={nom} onChangeText={setNom}
          placeholder={t('repasPlan.phNomMenu')} placeholderTextColor={UI.faint} />

        <SectionPliable titre={t('repasPlan.moments')} emoji="⏱">
          <Text style={s.aide}>{t('repasPlan.momentsAide')}</Text>
          {moments.map((mo, i) => (
            <View key={mo.id} style={s.ligneMoment}>
              <TextInput style={[s.input, { flex: 2 }]} value={mo.label} onChangeText={v => setMoment(i, 'label', v)}
                placeholder={t('repasPlan.phMoment')} placeholderTextColor={UI.faint} />
              <TextInput style={[s.input, { flex: 1 }]} value={mo.heureDebut || ''} onChangeText={v => setMoment(i, 'heureDebut', v)}
                placeholder={t('repasPlan.phHeure')} placeholderTextColor={UI.faint} />
              <TextInput style={[s.input, { flex: 1 }]} value={mo.heureFin || ''} onChangeText={v => setMoment(i, 'heureFin', v)}
                placeholder={t('repasPlan.phHeure')} placeholderTextColor={UI.faint} />
              <TouchableOpacity style={s.retirer} onPress={() => retirerMoment(i)}><Text style={s.retirerTxt}>✕</Text></TouchableOpacity>
            </View>
          ))}
          <TouchableOpacity style={s.ajouter} onPress={ajouterMoment}>
            <Text style={s.ajouterTxt}>＋ {t('repasPlan.ajouterMoment')}</Text>
          </TouchableOpacity>
          {moments.length === 0 && <Text style={s.attention}>{t('repasPlan.momentsRequis')}</Text>}
        </SectionPliable>

        <GrilleMenu moments={moments} cases={cases} libelleCase={libelleCase}
          onTapCase={(jourIdx, momentId) => setCaseOuverte({ jourIdx, momentId })} />
      </ScrollView>

      <FeuilleCase visible={!!caseOuverte} cellule={caseOuverte ? cases[cleOuverte] : null}
        repasList={repasList} recettes={recettes}
        onEnregistrer={enregistrerCase} onVider={viderCaseOuverte} onFermer={() => setCaseOuverte(null)} />
    </View>
  );
}

const makeStyles = (UI) => StyleSheet.create({
  ...stylesForm(UI),
  corps: { padding: 12, paddingBottom: 30 },
  aide: { fontSize: 12, color: UI.faint, lineHeight: 17, marginBottom: 8 },
  ligneMoment: { flexDirection: 'row', gap: 6, marginBottom: 6, alignItems: 'center' },
  retirer: { width: 30, height: 30, borderRadius: 8, alignItems: 'center', justifyContent: 'center' },
  retirerTxt: { fontSize: 16, color: UI.danger || '#c0392b' },
  ajouter: { paddingVertical: 9, alignItems: 'center' },
  ajouterTxt: { fontSize: 13.5, fontWeight: '700', color: UI.accent },
  attention: { fontSize: 12.5, color: UI.danger || '#c0392b', fontWeight: '700', marginTop: 4 },
});
